import moment from "moment";

import { CURRENCY_CODE_COELSA } from "constants.js";
import { echeqConceptLabel } from "util/checks";
import * as i18n from "util/i18n";
import { parseFloatToAmountFormat } from "util/number";
import { getGeneralTicketItem } from "util/transaction";
import buildEcheqTicketPdfData from "v3/pages/echeqs/echeqTicketPdfData";

const FORM_ID = "echeq.transaction";
const BATCH_DETAIL_FORM_ID = "echeqs.massives.manage.batchdetail";
const CESION_ACTIVITY = "cesion.echeq.activity";
const ENDORSE_ACTIVITY = "endorse.echeq.activity";

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : String(value);
};

const formatDateTime = (value) => {
    if (!value) {
        return "";
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm:ss") : String(value);
};

const formatAmount = (amount, currencyCode) => {
    if (amount == null || amount === "") {
        return "";
    }
    const currencyLabel = currencyCode === CURRENCY_CODE_COELSA.USD ? "USD" : "ARS";
    return `${currencyLabel} ${parseFloatToAmountFormat(String(amount))}`;
};

const resolveTransactionSubmitDateTime = (transaction) =>
    transaction?.submitDateTime ||
    transaction?.submitDateTimeAsString ||
    transaction?.modificationDateTime ||
    transaction?.creationDateTime;

export const resolveTransactionData = (transaction) => ({
    ...(transaction?.data || {}),
    ...(transaction || {}),
});

export const isStructuredReceiptCacheEntry = (entry) =>
    entry != null &&
    typeof entry === "object" &&
    !Array.isArray(entry) &&
    ("receipt" in entry || "transaction" in entry);

export const getReceiptFromCacheEntry = (entry) => {
    if (!entry) {
        return null;
    }

    if (isStructuredReceiptCacheEntry(entry)) {
        return entry.receipt ?? null;
    }

    return entry;
};

export const getTransactionFromCacheEntry = (entry) => {
    if (!isStructuredReceiptCacheEntry(entry)) {
        return null;
    }

    return entry.transaction ?? null;
};

const parseCesionPayload = (cesion) => {
    if (typeof cesion === "string") {
        try {
            return JSON.parse(cesion);
        } catch (error) {
            return null;
        }
    }
    if (cesion && typeof cesion === "object" && !Array.isArray(cesion)) {
        return cesion;
    }
    return null;
};

const resolveBeneficiary = (data) => {
    const beneficiary = data?.beneficiary;
    if (beneficiary && typeof beneficiary === "object") {
        return {
            reference: beneficiary.reference || data.beneficiaryName || "",
            document: beneficiary.document || "",
        };
    }
    if (typeof beneficiary === "string" && beneficiary.trim()) {
        return {
            reference: data.beneficiaryName || "",
            document: beneficiary.trim(),
        };
    }
    return {
        reference: data?.beneficiaryName || "",
        document: "",
    };
};

/** Same shape {@code createEcheqTransactionData} leaves in Redux for history tickets. */
const toHistoryTicketTransaction = (rawTransaction) => {
    const data = rawTransaction?.data || {};
    const echeq = data.echeq || (Array.isArray(data.echeqList) ? data.echeqList[0] : null);

    return {
        ...data,
        ...rawTransaction,
        idActivity: rawTransaction?.idActivity || data.idActivity,
        idTransaction: rawTransaction?.idTransaction,
        submitDateTime: resolveTransactionSubmitDateTime(rawTransaction),
        creationDate: rawTransaction?.creationDateTime || data.creationDate,
        echeq,
        cesion: parseCesionPayload(data.cesion || rawTransaction?.cesion),
        beneficiary: resolveBeneficiary({ ...rawTransaction, ...data }),
    };
};

/**
 * Pay (endoso/cesión) reuses the history PDF. Admit keeps {@code EcheqTransaction.exportPdf}.
 */
export function buildEcheqActionTransactionTicketPdfData(rawTransaction) {
    const idActivity = rawTransaction?.idActivity || rawTransaction?.data?.idActivity;
    if (idActivity === CESION_ACTIVITY || idActivity === ENDORSE_ACTIVITY) {
        return buildEcheqTicketPdfData(toHistoryTicketTransaction(rawTransaction));
    }

    const transactionData = resolveTransactionData(rawTransaction);
    const echeq = transactionData?.echeqList?.[0] || transactionData;
    const chequeConcepto = echeq.cheque_concepto;
    const concept = chequeConcepto ? `${chequeConcepto} ${echeqConceptLabel(chequeConcepto, "-")}` : "";
    const isAdmittedCesion = ["echeq.admit-cesion.action"].includes(idActivity);
    const cuit = `CUIT ${echeq?.emisor_cuit}`;
    const sender = echeq?.emisor_razon_social;

    const bodyData = [
        getGeneralTicketItem(`forms.transaction.ticket.date`, transactionData.submitDateTime, "datefulltime"),
        getGeneralTicketItem(
            `pay.paymentAFIP.idTransaction`,
            (rawTransaction?.idTransaction || transactionData?.idTransaction || "").substring(0, 8).toUpperCase(),
            "string",
        ),
        getGeneralTicketItem(`${FORM_ID}.echeq.number`, echeq?.cheque_numero, "string"),
        getGeneralTicketItem(`${FORM_ID}.echeq.id`, echeq?.cheque_id, "string"),
        getGeneralTicketItem(
            `${FORM_ID}.amount`,
            echeq?.monto,
            echeq?.moneda === CURRENCY_CODE_COELSA.USD ? "amountUSD" : "amountARS",
        ),
        getGeneralTicketItem(`${FORM_ID}.emision`, echeq?.fecha_emision, "date"),
        getGeneralTicketItem(`${FORM_ID}.payed`, echeq?.fecha_pago, "date"),
        getGeneralTicketItem(
            `${FORM_ID}.echeq.type`,
            i18n.get(`echeqs.emit.${String(echeq?.cheque_tipo || "").toLowerCase()}.label`),
            "string",
        ),
        getGeneralTicketItem(`${FORM_ID}.echeq.character`, echeq?.cheque_caracter, "string"),
        getGeneralTicketItem(`${FORM_ID}.mode`, i18n.get(`echeqs.${echeq?.cheque_modo}.modo`), "string"),
    ];

    if (isAdmittedCesion) {
        bodyData.push(
            getGeneralTicketItem(`echeqs.document.label`, cuit, "string"),
            getGeneralTicketItem(`echeqs.sender.label`, sender, "string"),
        );
    }

    bodyData.push(
        getGeneralTicketItem(`${FORM_ID}.concept`, concept, "string"),
        getGeneralTicketItem(`${FORM_ID}.motive`, echeq?.cheque_motivo_pago, "string"),
    );

    if (idActivity === "echeq.request-certificate.action" && echeq?.cod_visualizacion) {
        bodyData.push(getGeneralTicketItem(`${FORM_ID}.displayCode`, echeq?.cod_visualizacion, "string"));
    }

    if (idActivity === "echeq.repudiate.action") {
        bodyData.push(getGeneralTicketItem(`echeqs.repudiate_motive`, echeq?.specialAttribute, "string"));
    }

    if (transactionData?.coelsaCode) {
        bodyData.push(
            getGeneralTicketItem(`${FORM_ID}.coelsa.response`, transactionData?.coelsaResponse, "string"),
            getGeneralTicketItem(`${FORM_ID}.coelsa.code`, transactionData?.coelsaCode, "string"),
        );
    }

    if (idActivity === "echeq.deposit.action" || idActivity === "echeq.custody.action") {
        bodyData.push(
            getGeneralTicketItem(
                `echeqs.${transactionData?.action?.toLowerCase()}.account`,
                transactionData?.specialAttribute,
                "string",
            ),
        );
    }

    return {
        ticketName: i18n.get(`activities.${idActivity}`),
        ticketBody: bodyData,
    };
}

const mapSignatures = (transaction) => {
    const creatorFullName =
        transaction.creatorFullName ||
        (transaction.userCreatorLastName && transaction.userCreatorFirstName
            ? `${transaction.userCreatorLastName}, ${transaction.userCreatorFirstName}`
            : "");

    const submitDateTime = resolveTransactionSubmitDateTime(transaction);

    const preparedBy = creatorFullName
        ? {
              fullName: creatorFullName,
              at: formatDateTime(submitDateTime),
          }
        : undefined;

    const signedBy = (transaction.signatures || []).map((signature) => ({
        fullName: signature.fullName || signature.userFullName || "",
        at: formatDateTime(signature.signatureDate || signature.creationDateTime || submitDateTime),
    }));

    return { preparedBy, signedBy: signedBy.length > 0 ? signedBy : undefined };
};

/**
 * Maps {@code core.readTransaction} payload + batch detail row into UI Kit receipt shape.
 */
export default function mapTransactionToBatchDetailReceipt(transaction, item) {
    const tx = resolveTransactionData(transaction);
    const displayStatus = item?.status || "FALLIDO";
    const idActivity = transaction?.idActivity || tx.idActivity;
    const title = idActivity ? i18n.get(`activities.${idActivity}`, idActivity) : "";

    const statusText =
        displayStatus === "FINALIZADO"
            ? i18n.get(`${BATCH_DETAIL_FORM_ID}.receipt.status.finished`)
            : i18n.get(`${BATCH_DETAIL_FORM_ID}.receipt.status.failed`);

    const chequeConcepto = tx.cheque_concepto;
    const concept = chequeConcepto ? `${chequeConcepto} ${echeqConceptLabel(chequeConcepto, "-")}` : "";

    const rows = [
        { text: i18n.get(`${FORM_ID}.echeq.id`), node: tx.cheque_id || item?.idEcheq || "" },
        {
            text: i18n.get(`${BATCH_DETAIL_FORM_ID}.totalAmount`),
            node: formatAmount(tx.monto ?? item?.amount, tx.moneda ?? item?.currency),
        },
        { text: i18n.get(`${FORM_ID}.emision`), node: formatDate(tx.fecha_emision ?? item?.creationDate) },
        { text: i18n.get(`${FORM_ID}.payed`), node: formatDate(tx.fecha_pago ?? item?.paymentDate) },
    ];

    if (tx.cheque_tipo) {
        rows.push({
            text: i18n.get(`${FORM_ID}.echeq.type`),
            node: i18n.get(`echeqs.emit.${String(tx.cheque_tipo).toLowerCase()}.label`, tx.cheque_tipo),
        });
    }

    if (tx.cheque_caracter || item?.character) {
        rows.push({
            text: i18n.get(`${FORM_ID}.echeq.character`),
            node: tx.cheque_caracter || item?.character || "",
        });
    }

    if (tx.cheque_modo) {
        rows.push({
            text: i18n.get(`${FORM_ID}.mode`),
            node: i18n.get(`echeqs.${tx.cheque_modo}.modo`, tx.cheque_modo),
        });
    }

    if (concept) {
        rows.push({ text: i18n.get(`${FORM_ID}.concept`), node: concept });
    }

    if (tx.cheque_motivo_pago) {
        rows.push({ text: i18n.get(`${FORM_ID}.motive`), node: tx.cheque_motivo_pago });
    }

    if (displayStatus === "FALLIDO") {
        const coelsaCode = tx.voucherErrorCode || item?.voucherServiceErrorCode;
        const coelsaResponse = tx.voucherErrorMessage || item?.voucherServiceMessage;

        if (coelsaCode) {
            rows.push({
                text: i18n.get("failedVoucher.error.code.label"),
                node: coelsaCode,
            });
        }

        if (coelsaResponse) {
            rows.push({
                text: i18n.get("failedVoucher.error.message.label"),
                node: coelsaResponse,
            });
        }
    }

    const { preparedBy, signedBy } = mapSignatures(transaction);
    const submitDateTime = resolveTransactionSubmitDateTime(transaction);

    return {
        status: displayStatus,
        statusText,
        title,
        dateLabel: i18n.get("forms.transaction.ticket.date"),
        date: formatDateTime(submitDateTime),
        ticketNumberLabel: i18n.get("negotiate.transaction.voucher.label"),
        ticketNumber: (transaction.idTransaction || "").substring(0, 8).toUpperCase(),
        rows,
        preparedBy,
        signedBy,
    };
}
